/**
 * ReservationDetail.jsx — 에어카텔 예약 상세 (Phase 2, 2026-04-30)
 *
 * URL: /admin/reservations/:code
 * 권한: AIRTEL_RESERVATION_MANAGEMENT (admin 자동 통과)
 *
 * 구성:
 *   - 좌측: MasterPanel (마스터 정보)
 *   - 우측 4 탭: 항공 / 렌트카 / 호텔 / 결제
 *   - 하단: 정산 정보
 *
 * API: GET /api/admin/reservations/:code
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/apiClient';
import MasterPanel from './reservationmgmt/MasterPanel';
import FlightsTab  from './reservationmgmt/FlightsTab';
import HotelsTab   from './reservationmgmt/HotelsTab';
import CarsTab     from './reservationmgmt/CarsTab';
import PaymentsTab from './reservationmgmt/PaymentsTab';
import RevenueEventsPanel from './reservationmgmt/RevenueEventsPanel';
import { useAppPermissions } from '../../hooks/useAppPermissions';
import {
  detailBackButtonStyle,
  detailCodeStyle,
  detailCustomerStyle,
  detailHeaderStyle,
  detailMainGridStyle,
  detailPageStyle,
  detailStatusStyle,
  detailTitleRowStyle,
  tabBodyStyle,
  tabButtonStyle,
  tabHeaderStyle,
  tabShellStyle,
} from './reservationmgmt/ReservationDetail.mobileStyles';

const TABS = [
  { key: 'flights', label: '항공' },
  { key: 'cars',    label: '렌트카' },
  { key: 'hotels',  label: '호텔' },
  { key: 'payments', label: '결제' },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isMobile;
}

export default function ReservationDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('flights');
  const isMobile = useIsMobile();
  const { me, permCodes, loaded: permissionsLoaded } = useAppPermissions();
  const canUseReservationRevenue = me?.role === 'admin' || permCodes?.has('AIRTEL_RESERVATION_REVENUE');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/api/admin/reservations/${encodeURIComponent(code)}`);
      if (res.ok && res.data?.success) {
        setData(res.data.data);
      } else {
        setError(res.data?.message || '조회 실패');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>로딩 중...</div>;
  if (error)   return <div style={{ padding: 24, color: '#dc2626' }}>오류: {error}</div>;
  if (!data)   return <div style={{ padding: 24, color: 'var(--text-muted)' }}>데이터 없음</div>;

  const counts = {
    flights: data.flights.length,
    hotels:  data.hotels.length,
    cars:    data.cars.length,
    payments: data.payments?.length || 0,
  };
  const revenueBaseline = {
    flight_total: data.master.flight_total,
    car_total: data.master.car_total,
    hotel_total: data.master.hotel_total,
    customer_paid: data.master.customer_paid,
    customer_paid_total: data.master.customer_paid_total,
    revenue_event_payment_total: data.master.revenue_event_payment_total,
    difference: data.master.difference,
    profit: data.master.profit,
    summary_profit: data.master.summary_profit,
  };

  return (
    <div style={detailPageStyle(isMobile)}>
      {/* 헤더 */}
      <div style={detailHeaderStyle(isMobile)}>
        <div style={detailTitleRowStyle}>
          <button onClick={() => navigate('/admin/reservations')}
            style={detailBackButtonStyle}>
            ← 목록
          </button>
          <span style={detailCodeStyle}>{data.master.travel_code}</span>
          <span style={detailCustomerStyle}>{data.master.customer_name}</span>
        </div>
        <span style={detailStatusStyle}>
          {data.master.status}
        </span>
      </div>

      {/* 좌우 — 마스터 + 탭 */}
      <div style={detailMainGridStyle(isMobile)}>
        <MasterPanel master={data.master} onChanged={load} />

        <div style={tabShellStyle}>
          {/* 탭 헤더 */}
          <div style={tabHeaderStyle(isMobile)}>
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={tabButtonStyle(tab === t.key, isMobile)}>
                {t.label} ({counts[t.key]})
              </button>
            ))}
          </div>

          {/* 탭 본문 */}
          <div style={tabBodyStyle(isMobile)}>
            {tab === 'flights' && <FlightsTab rows={data.flights} travelCode={data.master.travel_code} onChanged={load} isMobile={isMobile} />}
            {tab === 'hotels'  && <HotelsTab  rows={data.hotels}  travelCode={data.master.travel_code} onChanged={load} isMobile={isMobile} />}
            {tab === 'cars'    && <CarsTab    rows={data.cars}    travelCode={data.master.travel_code} onChanged={load} isMobile={isMobile} />}
            {tab === 'payments' && <PaymentsTab payments={data.payments || []} travelCode={data.master.travel_code} onChanged={load} isMobile={isMobile} />}
          </div>
        </div>
      </div>

      {permissionsLoaded && canUseReservationRevenue && (
        <RevenueEventsPanel
          rows={data.revenue_events || []}
          travelCode={data.master.travel_code}
          baseline={revenueBaseline}
          flights={data.flights || []}
          hotels={data.hotels || []}
          masterNotes={data.master.notes || ''}
          onChanged={load}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}
